import React from 'react'
import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { Button } from '../../ui/button'
import { Label } from '../../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import { Checkbox } from '../../ui/checkbox'

const DAYS_OF_WEEK = [
  { value: 'Mon', label: 'Monday' },
  { value: 'Tue', label: 'Tuesday' },
  { value: 'Wed', label: 'Wednesday' },
  { value: 'Thu', label: 'Thursday' },
  { value: 'Fri', label: 'Friday' },
  { value: 'Sat', label: 'Saturday' },
  { value: 'Sun', label: 'Sunday' }
]

function DailyClassRosterForm({ form, onSubmit, isSubmitting, demo = false }) {
  const { facility_id: facilityId } = useParams()
  const { control, handleSubmit, watch } = form

  const selectedDays = watch('days_of_week') || []

  if (!facilityId) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>This report is only available when viewing a specific facility.</p>
        <p className="text-sm mt-2">Please access this report from a facility&apos;s reports page.</p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label>
            Days of Week <span className="text-red-500">*</span>
          </Label>
          <Controller
            name="days_of_week"
            control={control}
            rules={{
              validate: (value) => (value && value.length > 0) || 'Please select at least one day'
            }}
            render={({ field, fieldState }) => (
              <>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {DAYS_OF_WEEK.map((day) => (
                    <div key={day.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`day-${day.value}`}
                        checked={field.value?.includes(day.value)}
                        onCheckedChange={(checked) => {
                          const currentDays = field.value || []
                          if (checked) {
                            field.onChange([...currentDays, day.value])
                          } else {
                            field.onChange(currentDays.filter((d) => d !== day.value))
                          }
                        }}
                      />
                      <Label htmlFor={`day-${day.value}`} className="font-normal cursor-pointer">
                        {day.label}
                      </Label>
                    </div>
                  ))}
                </div>
                {fieldState.error && <p className="text-sm text-red-500 mt-1">{fieldState.error.message}</p>}
              </>
            )}
          />
          <p className="text-sm text-gray-500 mt-2">
            Select one or more days to include all classes on those days.
            {selectedDays.length > 0 && (
              <span className="block mt-1 font-medium text-purple-600">Selected: {selectedDays.join(', ')}</span>
            )}
          </p>
        </div>

        <div>
          <Label htmlFor="file_format">File Format</Label>
          <Controller
            name="file_format"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="xls">Excel (XLS)</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div hidden={demo}>
          <Label>Delivery Method</Label>
          <Controller
            name="delivery_method"
            control={control}
            render={({ field }) => (
              <div className="space-y-2 mt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="email"
                    checked={field.value === 'email' || field.value === 'both'}
                    disabled={field.value === 'email'}
                    onCheckedChange={(checked) => {
                      if (checked && field.value === 'download') {
                        field.onChange('both')
                      } else if (checked) {
                        field.onChange('email')
                      } else if (field.value === 'both') {
                        field.onChange('download')
                      }
                    }}
                  />
                  <Label htmlFor="email" className="font-normal cursor-pointer">
                    Email me when ready
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="download"
                    checked={field.value === 'download' || field.value === 'both'}
                    disabled={field.value === 'download'}
                    onCheckedChange={(checked) => {
                      if (checked && field.value === 'email') {
                        field.onChange('both')
                      } else if (checked) {
                        field.onChange('download')
                      } else if (field.value === 'both') {
                        field.onChange('email')
                      }
                    }}
                  />
                  <Label htmlFor="download" className="font-normal cursor-pointer">
                    Available for download
                  </Label>
                </div>
              </div>
            )}
          />
        </div>
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? 'Generating Preview...' : 'Preview Report'}
      </Button>
    </form>
  )
}

DailyClassRosterForm.propTypes = {
  demo: PropTypes.bool,
  form: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired
}

export default DailyClassRosterForm
